import { computed, Injectable, signal } from '@angular/core';
import type { AppChannel } from '@pressteaze/domain';
import type { AuthenticatedUser, OperatorSession } from './auth.models';

const operatorTokenKey = 'pressteaze.operator.session';

@Injectable({ providedIn: 'root' })
export class AuthStateService {
	private readonly configurationUserState = signal<AuthenticatedUser | null>(
		null,
	);
	private readonly operatorSessionState = signal<OperatorSession | null>(null);
	private readonly operatorTokenState = signal<string | null>(
		this.readOperatorToken(),
	);

	readonly configurationUser = this.configurationUserState.asReadonly();
	readonly operatorSession = this.operatorSessionState.asReadonly();
	readonly isConfigurationAuthenticated = computed(
		() => this.configurationUserState() !== null,
	);
	readonly isOperatorAuthenticated = computed(
		() =>
			this.operatorSessionState() !== null &&
			this.operatorTokenState() !== null,
	);

	hasChannel(channel: AppChannel): boolean {
		if (channel === 'operator' && this.isOperatorAuthenticated()) return true;
		return this.configurationUserState()?.channels.includes(channel) ?? false;
	}

	setConfigurationUser(user: AuthenticatedUser): void {
		this.configurationUserState.set(user);
	}

	setOperatorSession(token: string, session: OperatorSession): void {
		this.operatorTokenState.set(token);
		this.operatorSessionState.set(session);
		if (typeof sessionStorage !== 'undefined')
			sessionStorage.setItem(operatorTokenKey, token);
	}

	operatorToken(): string | null {
		return this.operatorTokenState();
	}

	clearConfiguration(): void {
		this.configurationUserState.set(null);
	}

	clearOperator(): void {
		this.operatorTokenState.set(null);
		this.operatorSessionState.set(null);
		if (typeof sessionStorage !== 'undefined')
			sessionStorage.removeItem(operatorTokenKey);
	}

	private readOperatorToken(): string | null {
		return typeof sessionStorage === 'undefined'
			? null
			: sessionStorage.getItem(operatorTokenKey);
	}
}
